package com.parmartechnologies.commerce.feature.cart.viewModel

import com.parmartechnologies.business.feature.business.handler.BusinessHandler
import com.parmartechnologies.core.feature.auth.model.FriendlyUser
import com.parmartechnologies.core.feature.cart.model.CartItem
import com.parmartechnologies.core.feature.cart.network.CreateCustomerInvoiceRequest
import com.parmartechnologies.core.feature.inventory.inventory.model.Inventory
import com.parmartechnologies.core.feature.product.model.Product
import com.parmartechnologies.core.feature.product.model.ProductPrice
import com.parmartechnologies.core.feature.sale.model.Sale
import com.parmartechnologies.core.app.AppViewModel
import com.parmartechnologies.commerce.feature.cart.handler.CartHandler
import com.parmartechnologies.commerce.feature.cart.network.CartNetwork
import com.parmartechnologies.commerce.feature.cart.repository.CartRepository
import com.parmartechnologies.commerce.feature.customer.handler.CustomerHandler
import com.parmartechnologies.commerce.feature.inventory.viewModel.inventory.InventoryViewModel
import com.parmartechnologies.commerce.feature.invoice.handler.InvoiceHandler
import com.parmartechnologies.communication.core.handler.AuthHandler
import com.parmartechnologies.core.engine.pricing.PricingEngine
import com.parmartechnologies.core.feature.business.model.Business
import com.parmartechnologies.core.feature.cart.model.CartStoreObject
import com.parmartechnologies.core.navigation.NavigationParam
import com.parmartechnologies.storage.store.UserDefaults
import com.parmartechnologies.storage.table.cart.CartItemDatabaseTable
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

class CartViewModel(val business: Business):
    AppViewModel() {

    private val databaseTable = CartItemDatabaseTable()
    private val repository: CartRepository = CartHandler.shared().repository

    val searchQuery: StateFlow<String>
        get() = repository.searchQuery
    val cartItems: StateFlow<List<CartItem>>
        get() = repository.cartItems

    val mrp: StateFlow<Double>
        get() = repository.mrp

    val discount: StateFlow<Double>
        get() = repository.discount

    val price: StateFlow<Double>
        get() = repository.price

    val subtotal: StateFlow<Double>
        get() = repository.subtotal

    val tax: StateFlow<Double>
        get() = repository.tax

    val instantDiscount: StateFlow<Double>
        get() = repository.instantDiscount

    val totalAmount: StateFlow<Double>
        get() = repository.totalAmount

    private fun updatePricesInCart(items: List<CartItem>) {
        var totalMrp = 0.0
        var subTotal = 0.0
        var price = 0.0
        var discount = 0.0
        var totalTax = 0.0
        var finalPrice = 0.0
        var instantDiscountPrice = 0.0
        items.forEach { cartItem ->
            if (cartItem.price?.Tax != null) {
                totalTax += (cartItem.price?.Tax!! * cartItem.quantity)
            }
            if (cartItem.price?.Price != null) {
                price += (cartItem.price?.Price!! * cartItem.quantity)
            }
            if (cartItem.price?.SalePrice != null) {
                subTotal += (cartItem.price?.SalePrice!! * cartItem.quantity)
            }
            if (cartItem.price?.Discount != null) {
                discount += (cartItem.price?.Discount!!)
            }
            if (cartItem.price?.FinalPrice != null) {
                finalPrice += (cartItem.price?.FinalPrice!! * cartItem.quantity)
            }
            if (cartItem.price?.MRP != null) {
                totalMrp += (cartItem.price?.MRP!! * cartItem.quantity)
            }
        }
        instantDiscountPrice = instantDiscount.value
        finalPrice -= instantDiscountPrice
        scope.launch {
            repository.subtotalLiveData.emit(subTotal)
            repository.discountLiveData.emit(discount)
            repository.taxLiveData.emit(totalTax)
            repository.totalAmountLiveData.emit(finalPrice)
            repository.mrpLiveData.emit(totalMrp)
            repository.priceLiveData.emit(price)
        }
    }

    override fun changeSearchQuery(value: String) {
        scope.launch {
            repository.searchQueryState.emit(value)
        }
        performSearch(value)
    }

    fun isOutOfStock(product: Product, selectedCartItem: CartItem? = null): Boolean {
        val inventoryViewModel = InventoryViewModel(business)
        val productInventory =
            inventoryViewModel.allInventory.value.filter { inventory: Inventory -> inventory.ProductID == product._id }
        var numberOutOutOfStock = 0
        productInventory.forEach { item ->
            cartItems.value.forEach { cartItem ->
                if (item._id == cartItem.inventory._id) {
                    if (cartItem.quantity >= item.Quantity) {
                        numberOutOutOfStock += 1
                    }
                } else if (item.ProductID == product._id && item.Quantity <= 0) {
                    numberOutOutOfStock += 1
                }
            }
        }
        if (productInventory.isEmpty() && selectedCartItem != null) {
            if (product.Quantity <= selectedCartItem.quantity) {
                return true
            } else {
                return false
            }
        } else if (productInventory.isEmpty() && product.Quantity.toInt() == 0) {
            return true
        } else if (productInventory.isEmpty() && product.Quantity.toInt() != 0) {
            return false
        }
        return numberOutOutOfStock == productInventory.size
    }

    fun isOutOfStock(allInv: List<Inventory>): Boolean {
        var isOut = true
        allInv.forEach { item ->
            if (item.Quantity > 0) {
                isOut = false
            }
        }
        return isOut
    }

    fun isOutOfStock(allInv: List<Inventory>, cartItem: CartItem): Boolean {
        var numberOutOutOfStock = 0
        allInv.forEach { item ->
            if (item._id == cartItem.inventory._id) {
                if (cartItem.quantity >= item.Quantity) {
                    numberOutOutOfStock += 1
                }
            } else if (item.Quantity <= 0) {
                numberOutOutOfStock += 1
            }
        }
        return numberOutOutOfStock == allInv.size
    }

    fun isOutOfStock(item: CartItem): Boolean {
        if (item.inventory._id.isNotEmpty()) {
            val inventoryViewModel = InventoryViewModel(business)
            val productInventory =
                inventoryViewModel.allInventory.value.filter { inventory: Inventory -> inventory.ProductID == item.product._id }
            if (productInventory.size > 1) {
                return isOutOfStock(productInventory, cartItem = item)
            } else if (productInventory.size == 1) {
                if (productInventory.first().Quantity <= item.quantity) {
                    return true
                }
            } else {
                return false
            }
        } else {
            if (item.quantity >= item.product.Quantity) {
                return true
            } else {
                return false
            }
        }
        return false
    }

    fun getProductQuantity(product: Product): Int {
        var quantity = 0
        if (cartItems.value.isNotEmpty()) {
            cartItems.value.forEach {
                if (product._id == it.product._id) {
                    quantity = it.quantity
                }
            }
        }
        return quantity
    }

    fun getProductPrice(product: Product): Double {
        var price = 0.0
        if (cartItems.value.isNotEmpty()) {
            cartItems.value.forEach {
                if (product._id == it.product._id) {
                    price = it.price?.FinalPrice ?: 0.0
                }
            }
        }
        return price
    }

    fun getItemForProduct(
        product: Product,
        inventory: Inventory? = null,
        callback: (CartItem?) -> Unit
    ) {
        val cartId = inventory?._id ?: product._id
        cartItems.value.forEach {
            if(it._id == cartId){
                callback(it)
            }
        }
    }

    fun getItemForProduct(product: Product): CartItem? {
        var itemToReturn: CartItem? = null
        cartItems.value.forEach { item ->
            if (item.product._id == product._id) {
                itemToReturn = item
            }
        }
        return itemToReturn
    }

    fun getItemForInventory(inventory: Inventory): CartItem? {
        var itemToReturn: CartItem? = null
        cartItems.value.forEach { item ->
            if (item.inventory._id == inventory._id) {
                itemToReturn = item
            }
        }
        return itemToReturn
    }


//    fun prepareOrderDetails(customer: Customer?): JSONObject? {
//        val unixTime = Clock.System.now().epochSeconds
//        val request = JSONObject()
//        val user = FriendlyUser()
//        val business = BusinessHandler.shared().repository.business.value
//        request.put(KeyConstant.UserID, user._id)
//        request.put(KeyConstant.BusinessID, business?._id)
//        val invoiceId = System.currentTimeMillis()
//        val transactions = prepareTransaction(customer, invoiceId)
//
//        if (CustomerHandler.shared().repository.customer.value != null) {
//            val customerJson = gson.toJson(CustomerHandler.shared().repository.customer.value)
//            request.put(KeyConstant.customer, JSONObject(customerJson))
//        }
//        request.put(KeyConstant.deviceId, AuthHandler.shared().deviceId)
//        request.put(KeyConstant.orderItems, transactions.first)
//        if (transactions.first.length() == 0) {
//            return null
//        }
//        request.put(KeyConstant.orderId, unixTime)
//        request.put(KeyConstant.pricing, transactions.second)
//        request.put(KeyConstant.UserID, user._id)
//        request.put(KeyConstant.MobileNumberNumber, user.MobileNumber)
//        request.put(KeyConstant.dialCode, user.dialCode)
//        request.put(KeyConstant.BusinessID, business?._id)
//        request.put(KeyConstant.toBusinessID, business?._id)
//        request.put(KeyConstant.business, JSONObject(gson.toJson(business)))
//        request.put(KeyConstant.instantDiscount, instantDiscount.value)
//        request.put(KeyConstant.deviceId, AuthHandler.shared().deviceId)
//        if (CustomerHandler.shared().repository.customer.value != null) {
//            val customerJson = gson.toJson(CustomerHandler.shared().repository.customer.value)
//            request.put(KeyConstant.customer, JSONObject(customerJson))
//        }
//        request.put(KeyConstant.deviceId, AuthHandler.shared().deviceId)
//        return request
//    }

    @OptIn(ExperimentalTime::class)
    fun createInvoice(callback: (Boolean) -> Unit) {
        val request = CreateCustomerInvoiceRequest()
        val user = AuthHandler.shared().repository.authUser.value
        val customer = CustomerHandler.shared().repository.selectedCustomer.value
        request.BusinessID = business._id
        val invoiceId = Clock.System.now().epochSeconds
        val transactions = prepareTransaction(
            invoiceId
        )
        request.InvoiceId = invoiceId
        request.Customer = customer
        request.DeviceID = AuthHandler.shared().deviceId
        if (transactions.first.isEmpty()) {
            return
        }
        request.Sales = transactions.first
        request.Pricing = transactions.second
        request.UserID = user._id
        request.BusinessID = business._id
        request.Business = business
        request.InstantDiscount = instantDiscount.value
        request.DeviceID = AuthHandler.shared().deviceId
        scope.launch {
            startLoading()
            CartNetwork().createCustomerInvoice(request) {
                stopLoading()
                if (it?.payload?._id?.isNotEmpty() == true) {
                    scope.launch {
                        repository.cartItemState.emit(arrayListOf())
                        InvoiceHandler.shared().repository.customerInvoiceLiveData.emit(it.payload)
                        callback(true)
                    }
                } else {
                    callback(false)
                }
            }
        }
    }


    @OptIn(ExperimentalTime::class)
    private fun prepareTransaction(
        invoiceId: Long
    ): Pair<List<Sale>, ProductPrice> {
        val user = FriendlyUser()
        val customer = CustomerHandler.shared().repository.selectedCustomer.value
        val transactions: ArrayList<Sale> = arrayListOf()
        val pricingDetails = ProductPrice()
        var totalTax = 0.0
        var totalCostPrice = 0.0
        var totalPrice = 0.0
        var totalSalePrice = 0.0
        var totalFinalPrice = 0.0
        if (cartItems.value.isNotEmpty()) {
            cartItems.value.forEach { cartItem ->
                // val productData = cartItem.product
                val transactionData = Sale()
                transactionData.InventoryID = cartItem.inventory._id
                transactionData.UserID = user._id
                transactionData.BusinessID = business._id
                if (customer != null) {
                    transactionData.CustomerID = customer._id
                    transactionData.CustomerName = customer.Name
                    transactionData.CustomerMobile = customer.MobileNumber
                    transactionData.VehicleNumber = customer.DeviceID
                }
                val quantity = cartItem.quantity
                val finalPrice = cartItem.price?.FinalPrice!! * quantity
                totalFinalPrice += finalPrice
                val costPrice = cartItem.price?.CostPrice!! * quantity
                totalCostPrice += costPrice
                val Price = cartItem.price?.SalePrice!! * quantity
                totalPrice += Price
                val salePrice = cartItem.price?.SalePrice!! * quantity
                totalSalePrice += salePrice
                val IGST = cartItem.price?.IGST ?: 0.0
                val IGSTValue = PricingEngine().computePercentToValue(finalPrice, IGST)
                val CGST = cartItem.price?.CGST ?: 0.0
                val CGSTValue = PricingEngine().computePercentToValue(finalPrice, CGST)
                val SGST = cartItem.price?.SGST ?: 0.0
                val SGSTValue = PricingEngine().computePercentToValue(finalPrice, SGST)
                val VAT = cartItem.price?.VAT ?: 0.0
                val VATValue = PricingEngine().computePercentToValue(finalPrice, VAT)
                val CESS = cartItem.price?.CESS ?: 0.0
                val CESSValue = PricingEngine().computePercentToValue(finalPrice, CESS)
                val Tax = cartItem.price?.Tax!! * quantity
                totalTax += Tax

                transactionData.InvoiceID = invoiceId.toString()
                transactionData.ProductID = cartItem.product._id
                transactionData.ProductName = cartItem.product.Name
//                transactionData.product = cartItem.product
//                transactionData.mode = "CASH"
                transactionData.Quantity = quantity
                transactionData.IGST = IGST
                transactionData.CGST = CGST
                transactionData.SGST = SGST
                transactionData.VAT = VAT
                transactionData.CESS = CESS
                transactionData.IGSTValue = IGSTValue
                transactionData.CGSTValue = CGSTValue
                transactionData.SGSTValue = SGSTValue
                transactionData.VATValue = VATValue
                transactionData.CESSValue = CESSValue
                transactionData.Tax = Tax
                transactionData.Price = cartItem.price!!.Price.toFloat()
                transactionData.SalePrice = cartItem.price!!.SalePrice.toFloat()
                transactionData.FinalPrice = finalPrice
//                transactionData.Co = cartItem.price?.CostPrice
//                transactionData.saleDate = now()
                transactionData.Discount = cartItem.price!!.Discount.toFloat()
                transactions.add(transactionData)
            }
        }
        pricingDetails.Tax = totalTax
        pricingDetails.FinalPrice = totalFinalPrice
        pricingDetails.CostPrice = totalCostPrice
        pricingDetails.Price = totalPrice
        pricingDetails.SalePrice = totalSalePrice
        return Pair(transactions, pricingDetails)
    }

    fun addToCart(
        product: Product,
        params: NavigationParam?,
        inventory: Inventory? = null,
        callback: (List<CartItem>?) -> Unit
    ) {
        scope.launch {
            val allItems: MutableList<CartItem> = mutableListOf()
            var currentItem: CartItem? = null
            cartItems.value.forEach { item ->
                if (item.product._id == product._id) {
                    currentItem = item
                }
            }
            if (currentItem != null) {
                currentItem.quantity += 1
            } else {
                currentItem = product.toCartItem(inventory)
                currentItem.quantity = 1
                currentItem.customerId = params?.customer!!._id
                currentItem.BusinessID = params?.business!!._id
                currentItem.selectedBusinessID = params.selectedBusiness._id
                currentItem.UserID = params.user._id
                allItems.add(currentItem)
            }
            if (inventory != null && inventory._id.isNotEmpty()) {
                currentItem.inventory = inventory
                currentItem.price = inventory.ProductPrice
            }


            cartItems.value.forEach { existing ->
                if (currentItem.product._id == existing.product._id) {
                    allItems.add(currentItem)
                } else {
                    allItems.add(existing)
                }
            }
            storeCart(params?.business, allItems)
            loadCart(params)
            callback(allItems)
        }
    }

    fun removeFromCart(product: Product, params: NavigationParam?, inventory: Inventory? = null) {
        scope.launch {
            val allItems: MutableList<CartItem> = mutableListOf()
            var currentItem: CartItem? = null
            cartItems.value.forEach { item ->
                if (item.product._id == product._id) {
                    currentItem = item
                }
            }
            if (currentItem != null) {
                currentItem.quantity -= 1
            }
            cartItems.value.forEach { item ->
                if (item.product._id == currentItem?.product?._id) {
                    if (currentItem.quantity > 0) {
                        allItems.add(currentItem)
                    }
                } else {
                    allItems.add(item)
                }
            }
            storeCart(
                params?.business,
                items = allItems
            )
            loadCart(params)
        }
    }

    fun loadCart(params: NavigationParam?, callBack: (List<CartItem>) -> Unit = {}) {
        scope.launch {
            repository.cartItemState.emit(listOf())
            val stringValue = UserDefaults.shared().retrieve("cart_${params?.business?._id}")
            print("cart from storage $stringValue")
            try {
                val cartStore: CartStoreObject = jsonDecoder.decodeFromString(stringValue)
                callBack(cartStore.items)
                scope.launch {
                    repository.cartItemState.emit(cartStore.items)
                }
                updatePricesInCart(cartStore.items)
            } catch (e: Exception) {
                print("Can't convert string to cart items")
                print(e.message)
                print("\n\n\n")
                print("stringValue : $stringValue")
                callBack(listOf())
            }
        }

    }

    fun storeCart(business: Business?, items: List<CartItem>) {
        val cartStore = CartStoreObject(items = items)
        val stringValue = jsonDecoder.encodeToString(cartStore)
        print("cart to storage $stringValue")
        var cartStoreName : String = "cart_"
        if(business != null && business._id.isNotEmpty()){
            cartStoreName += business._id
        }
        UserDefaults.shared().store(cartStoreName, stringValue)
    }

    fun resetCart(business: Business) {
        UserDefaults.shared().remove("cart_${business._id}")
    }
}